import { isString } from '@art-suite/art-core-ts';

import {
  ApiCreatePropertyProject,
  ApiFindPropertyProjects,
  ApiHandoffPropertyProject,
  ApiPropertyProject,
  ApiScan,
  ApiTakePropertyProjectWork,
  ApiUpdatePropertyProject,
  getActivePropertyProjectsByMarketIdQueryKey,
  getActivePropertyProjectsForUserQueryKey,
  getActivePropertyProjectsQueryKey,
  getProjectActivityLogQueryKeyPrefix,
  getPropertyProjectQueryKey,
  getPropertyProjectsQueryKey,
  getPropertyProjectsQueryKeyPrefix,
  getScanNotesQueryKey,
  getScanQueryKey,
  getUsersForScanQueryKey,
  KaiProApiClient,
  PropertyProjectQuery,
} from '@kaiizen/kai-pro-api';
import { useCachedQuery, useQueryCache, useQueryCacheMutation } from '@kaiizen/react-persistent-cache';

import { ScanOfflineDataMinTtlOnAccessMs } from '../apiScanAndTemplateState/ScanStateLib';
import { useCreateUpdateMutatorsWithQueryCache } from '../reactQueryLib';
import { useKaiProApiClient } from '../useKaiProApiClient';

export type ProjectsStateMutators = {
  create: (record: ApiCreatePropertyProject) => ApiPropertyProject;
  update: (id: string, updates: ApiUpdatePropertyProject) => ApiPropertyProject;
};

const usePropertyProjectMutators = (kaiProApiClient: KaiProApiClient) => {
  const scanQueryCache = useQueryCache<ApiScan>();
  const standardMutators = useCreateUpdateMutatorsWithQueryCache<ApiCreatePropertyProject, ApiUpdatePropertyProject, ApiPropertyProject>({
    apiCreate: kaiProApiClient.createPropertyProject.bind(kaiProApiClient),
    apiUpdate: kaiProApiClient.updatePropertyProject.bind(kaiProApiClient),
    pluralKeyPrefix: getPropertyProjectsQueryKeyPrefix(),
    getSingleKey: getPropertyProjectQueryKey,
    onRecordCreateOrUpdate: project => {
      if (project.latestScan) void scanQueryCache.cache.set(getScanQueryKey(project.latestScan.id), project.latestScan);
    },
  });
  const queryCache = useQueryCache<ApiPropertyProject>();
  const handoffMutation = useQueryCacheMutation(
    async ({ projectId, data }: { projectId: string; data: ApiHandoffPropertyProject }) => {
      const project = await kaiProApiClient.handoffPropertyProject(projectId, data);
      await queryCache.cache.set(getPropertyProjectQueryKey(project.id), project);
      await Promise.all([
        queryCache.refetchAll({ prefix: getPropertyProjectsQueryKeyPrefix() }),
        queryCache.refetchAll({ prefix: getUsersForScanQueryKey(data.scanId) }),
        queryCache.refetchAll({ prefix: getProjectActivityLogQueryKeyPrefix(project.id) }),
        queryCache.refetchAll({ prefix: getScanNotesQueryKey(data.scanId) }),
      ]);
      return project;
    },
  );
  const takeWorkMutation = useQueryCacheMutation(
    async ({ projectId, data }: { projectId: string; data: ApiTakePropertyProjectWork }) => {
      const project = await kaiProApiClient.takePropertyProjectWork(projectId, data);
      await queryCache.cache.set(getPropertyProjectQueryKey(project.id), project);
      await Promise.all([
        queryCache.refetchAll({ prefix: getPropertyProjectsQueryKeyPrefix() }),
        queryCache.refetchAll({ prefix: getUsersForScanQueryKey(data.scanId) }),
        queryCache.refetchAll({ prefix: getProjectActivityLogQueryKeyPrefix(project.id) }),
      ]);
      return project;
    },
  );

  return {
    ...standardMutators,
    handoff: (projectId: string, data: ApiHandoffPropertyProject) => handoffMutation.mutate({ projectId, data }),
    takeWork: (projectId: string, data: ApiTakePropertyProjectWork) => takeWorkMutation.mutate({ projectId, data }),
  };
};

export type PropertyProjectMutators = ReturnType<typeof usePropertyProjectMutators>;

export const usePropertyProject = (propertyProjectOrPropertyProjectId: ApiPropertyProject | string | undefined) => {
  const propertyProjectId = isString(propertyProjectOrPropertyProjectId) ? propertyProjectOrPropertyProjectId : propertyProjectOrPropertyProjectId?.id;
  const preloadData = (propertyProjectOrPropertyProjectId && propertyProjectOrPropertyProjectId !== propertyProjectId)
    ? propertyProjectOrPropertyProjectId as ApiPropertyProject
    : undefined;
  const kaiProApiClient = useKaiProApiClient();

  const query = useCachedQuery<ApiPropertyProject>(
    getPropertyProjectQueryKey(propertyProjectId),
    () => kaiProApiClient.getPropertyProject(propertyProjectId!),
    { enabled: !!kaiProApiClient.token && !!propertyProjectId, minTtlOnAccessMs: ScanOfflineDataMinTtlOnAccessMs, preloadData },
  );

  return {
    ...query,
    propertyProject: query.data,
    projectMutators: usePropertyProjectMutators(kaiProApiClient),
  };
};

export const useActivePropertyProjectsByMarketId = (orgMarketId?: string) => {
  const kaiProApiClient = useKaiProApiClient();

  const query = useCachedQuery<ApiPropertyProject[]>(
    getActivePropertyProjectsByMarketIdQueryKey(orgMarketId),
    () => kaiProApiClient.getActivePropertyProjectsByMarketId(orgMarketId!),
    { enabled: !!kaiProApiClient.token && !!orgMarketId },
  );

  return {
    ...query,
    activeProjects: query.data,
  };
};

export const useActivePropertyProjects = (
  options?: { orgMarketId?: string; limit?: number; search?: string; assignedToCurrentUser?: boolean; sortBy?: string },
) => {
  const kaiProApiClient = useKaiProApiClient();

  const query = useCachedQuery<ApiPropertyProject[]>(
    getActivePropertyProjectsQueryKey(options),
    () =>
      kaiProApiClient.getActivePropertyProjects({
        orgMarketId: options?.orgMarketId,
        limit: options?.limit,
        search: options?.search,
        assignedToCurrentUser: options?.assignedToCurrentUser,
        sortBy: options?.sortBy,
      }),
    { enabled: !!kaiProApiClient.token },
  );

  return {
    ...query,
    activeProjects: query.data,
  };
};

export const usePropertyProjects = (options?: PropertyProjectQuery & { enabled?: boolean }) => {
  const kaiProApiClient = useKaiProApiClient();
  const { enabled = true, ...propertyProjectQuery } = options ?? {};

  const key = getPropertyProjectsQueryKey(propertyProjectQuery);

  const query = useCachedQuery<ApiPropertyProject[]>(
    key,
    () => kaiProApiClient.getPropertyProjects(propertyProjectQuery),
    { enabled: !!kaiProApiClient.token && enabled },
  );

  return {
    ...query,
    propertyProjects: query.data,
    propertyProjectMutators: usePropertyProjectMutators(kaiProApiClient),
  };
};

export const useActivePropertyProjectsForUser = () => {
  const kaiProApiClient = useKaiProApiClient();

  const query = useCachedQuery<ApiPropertyProject[]>(
    getActivePropertyProjectsForUserQueryKey(),
    () => kaiProApiClient.getActivePropertyProjectsForUser(),
    { enabled: !!kaiProApiClient.token },
  );

  return {
    ...query,
    activeProjects: query.data,
  };
};

/**
 * Finds the current org's properties and projects from queries: project or property ids, customer property ids, or addresses.
 * One result per query, in order; see `KaiProApiEndpoints.findPropertyProjects` for the matching rules.
 */
export const useFindPropertyProjects = () => {
  const kaiProApiClient = useKaiProApiClient();
  return useQueryCacheMutation((payload: ApiFindPropertyProjects) => kaiProApiClient.findPropertyProjects(payload));
};
